import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { FlexCol, Flex } from '@/UI/styles';
import { useIsHovered } from '@/hooks/useIsHovered';
import { I18N_KEYS } from '@/language/keys';
import { EL_COLORS } from '@/colors';
import { layerPropertyGroups } from './layerProperties';
import { LayerPropertyInput } from './LayerPropertyInput';
import { LayerPropertyPanelField } from './LayerPropertyPanelField';
import { DimensionsLockAspectRatioButton } from './LockDimensionsAspectRatioButton';
import { AlignmentButtons } from './AlignmentButtons';
import { RotationControlsRow } from './RotationControlsRow';
import type { Layer, LayerId, NodeId } from 'web';
import type { PropertyKey, LayerPropertyLayoutRow, LayerPropertyLayoutSection } from './layerProperties';

type LayerPropertyGroupKey = keyof typeof layerPropertyGroups;

interface LayerPropertiesSectionProps {
  index: number;
  section: LayerPropertyLayoutSection;
  nodeId: NodeId;
  layerId: LayerId;
  layer: Layer;
}

export const LayerPropertiesSection = ({ index, section, nodeId, layerId, layer }: LayerPropertiesSectionProps) => {
  return (
    <FlexCol
      key={`section-${index}`}
      sx={{ borderTop: index !== 0 ? `1px solid ${EL_COLORS.DividerBorder}` : 'none', py: 2 }}
    >
      <FlexCol sx={{ gap: 2 }}>
        {section.map((row: LayerPropertyLayoutRow) => {
          const key = Array.isArray(row) ? row.join('-') : row;
          const rowHasDimensions = Array.isArray(row) ? row.includes('dimensions') : row === 'dimensions';
          return (
            <Fragment key={key}>
              <Row row={row} nodeId={nodeId} layerId={layerId} layer={layer} />
              {rowHasDimensions ? <AlignmentRow nodeId={nodeId} layerId={layerId} /> : null}
            </Fragment>
          );
        })}
      </FlexCol>
    </FlexCol>
  );
};

interface AlignmentRowProps {
  nodeId: NodeId;
  layerId: LayerId;
}

const AlignmentRow = ({ nodeId, layerId }: AlignmentRowProps) => {
  const { t } = useTranslation();
  return (
    <Flex sx={{ gap: 0.5, flexShrink: 1 }}>
      <LayerPropertyPanelField key="layer-alignment" label={t(I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.ALIGNMENT)}>
        <AlignmentButtons layerId={layerId} nodeId={nodeId} />
      </LayerPropertyPanelField>
    </Flex>
  );
};

const Row = ({
  row,
  nodeId,
  layerId,
  layer,
}: {
  row: LayerPropertyLayoutRow;
  nodeId: NodeId;
  layerId: LayerId;
  layer: Layer;
}) => {
  const { isHovered: isHovering, ...elementProps } = useIsHovered();

  const rowHasDimensions = Array.isArray(row) ? row.includes('dimensions') : row === 'dimensions';

  return (
    <Flex sx={{ gap: 0.5, flexShrink: 1 }} {...elementProps}>
      {Array.isArray(row) ? (
        <Flex sx={{ gap: 0.5 }}>
          {row.map((groupKey) =>
            groupKey === 'rotationControls' ? (
              <RotationControlsRow key={groupKey} nodeId={nodeId} layerId={layerId} layer={layer} />
            ) : (
              <Group nodeId={nodeId} layerId={layerId} layer={layer} groupKey={groupKey} key={groupKey} />
            ),
          )}
        </Flex>
      ) : row === 'rotationControls' ? (
        <RotationControlsRow nodeId={nodeId} layerId={layerId} layer={layer} />
      ) : (
        <Group fullWidth nodeId={nodeId} layerId={layerId} layer={layer} groupKey={row} key={row} />
      )}

      {rowHasDimensions ? (
        <Box sx={{ alignSelf: 'end' }}>
          <DimensionsLockAspectRatioButton layerId={layerId} nodeId={nodeId} layer={layer} isHovering={isHovering} />
        </Box>
      ) : null}
    </Flex>
  );
};

function Group({
  layerId,
  nodeId,
  layer,
  groupKey,
  fullWidth,
}: {
  groupKey: LayerPropertyGroupKey;
  nodeId: NodeId;
  layerId: LayerId;
  layer: Layer;
  fullWidth?: boolean;
}) {
  const { t } = useTranslation();
  const group = layerPropertyGroups[groupKey];
  return (
    <LayerPropertyPanelField key={groupKey} label={t(group.label)}>
      {group.properties.map((propKey: PropertyKey) => {
        return (
          <LayerPropertyInput
            key={`property-${groupKey}-${propKey}`}
            layerId={layerId}
            layer={layer}
            groupKey={groupKey}
            propKey={propKey}
            nodeId={nodeId}
            size={fullWidth ? 'large' : 'small'}
          />
        );
      })}
    </LayerPropertyPanelField>
  );
}
